import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AddMembersModal from "./AddMembersModal";
import "./style.css";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    // Avoid ObjectId.id (Buffer) — use hex string helpers instead.
    if (typeof value.toHexString === "function") {
      return value.toHexString();
    }
    if (value._id) return normalizeId(value._id);
    if (typeof value.toString === "function") {
      const asString = value.toString();
      if (asString && asString !== "[object Object]") return asString;
    }
  }
  return String(value);
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function GroupInfoModal({
  conversation,
  currentUser,
  groupMembers,
  groupOwnerId,
  groupAdminIds = [],
  isOpen,
  onClose,
  onOpenMemberProfile,
  canEditGroupPhoto,
  onChangeGroupPhoto,
  isUploadingGroupPhoto,
  groupPicture,
  onLeaveGroup,
  onDeleteGroup,
  userLeft = false,
  isCurrentUserAdmin = false,
  onRemoveMember,
  onAddMembers,
}) {
  const photoInputRef = useRef(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [viewerZoom, setViewerZoom] = useState(MIN_ZOOM);
  const [viewerOffset, setViewerOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const members = useMemo(() => {
    if (Array.isArray(groupMembers) && groupMembers.length > 0) {
      return groupMembers;
    }
    if (!conversation?.participants) return [];
    return conversation.participants;
  }, [groupMembers, conversation]);

  const ownerId = normalizeId(groupOwnerId || conversation?.admin);
  const conversationAdminId = normalizeId(conversation?.admin);
  const currentUserId = normalizeId(currentUser?.id || currentUser?._id);

  const adminIdSet = useMemo(() => {
    const ids = new Set();
    if (ownerId) ids.add(ownerId);
    if (conversationAdminId) ids.add(conversationAdminId);
    (Array.isArray(groupAdminIds) ? groupAdminIds : []).forEach((id) => {
      const normalized = normalizeId(id);
      if (normalized) ids.add(normalized);
    });
    (Array.isArray(members) ? members : []).forEach((member) => {
      if (member?.role === "admin") {
        const memberId = normalizeId(member?._id || member?.id);
        if (memberId) ids.add(memberId);
      }
    });
    return ids;
  }, [ownerId, conversationAdminId, groupAdminIds, members]);

  const getRoleLabel = (member) => {
    const memberId = normalizeId(member?._id || member?.id);
    if (!memberId) return "Member";
    if (adminIdSet.has(memberId) || member?.role === "admin") {
      return "Admin";
    }
    return "Member";
  };

  // Whether the current viewer can remove this specific member. Admins cannot
  // remove themselves or the group owner from here. To keep the UI predictable
  // we also hide the icon for other admins — promotion/demotion of admins is
  // handled elsewhere by the owner.
  const canRemoveMember = (member) => {
    if (!isCurrentUserAdmin || userLeft) return false;
    const memberId = normalizeId(member?._id || member?.id);
    if (!memberId) return false;
    if (memberId === currentUserId) return false;
    if (ownerId && memberId === ownerId) return false;
    if (getRoleLabel(member) === "Admin") return false;
    return true;
  };

  const customGroupPhoto =
    groupPicture ||
    conversation?.groupPicture ||
    conversation?.picture ||
    "";

  const hasCustomGroupPhoto = useMemo(() => {
    if (!customGroupPhoto || typeof customGroupPhoto !== "string") return false;
    const trimmed = customGroupPhoto.trim();
    if (!trimmed) return false;
    return !trimmed.includes("default_group.svg");
  }, [customGroupPhoto]);

  const groupPhotoSrc = hasCustomGroupPhoto
    ? customGroupPhoto
    : "/images/default_group.svg";

  const showNoPhotoMessage = () => {
    const notification = document.createElement("div");
    notification.textContent = "No group profile picture";
    notification.setAttribute("role", "status");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #495057;
      color: #fff;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10060;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 2500);
  };

  const closePhotoViewer = () => {
    setShowPhotoViewer(false);
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
  };

  const openPhotoViewer = () => {
    if (!hasCustomGroupPhoto) {
      showNoPhotoMessage();
      return;
    }
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
    setShowPhotoViewer(true);
  };

  const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

  const zoomBy = (delta) => {
    setViewerZoom((prev) => clampZoom(Number((prev + delta).toFixed(2))));
  };

  const handleViewerWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  };

  const handleViewerPointerDown = (e) => {
    if (viewerZoom <= MIN_ZOOM) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: viewerOffset.x,
      originY: viewerOffset.y,
    };
  };

  const handleViewerPointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag.dragging) return;
    setViewerOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const handleViewerPointerUp = () => {
    dragStateRef.current.dragging = false;
  };

  const handleViewerDoubleClick = () => {
    if (viewerZoom > MIN_ZOOM) {
      setViewerZoom(MIN_ZOOM);
      setViewerOffset({ x: 0, y: 0 });
    } else {
      setViewerZoom(2);
    }
  };

  useEffect(() => {
    if (viewerZoom <= MIN_ZOOM) {
      setViewerOffset({ x: 0, y: 0 });
    }
  }, [viewerZoom]);

  useEffect(() => {
    if (!showPhotoViewer) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closePhotoViewer();
      if (e.key === "+" || e.key === "=") zoomBy(ZOOM_STEP);
      if (e.key === "-" || e.key === "_") zoomBy(-ZOOM_STEP);
      if (e.key === "0") {
        setViewerZoom(MIN_ZOOM);
        setViewerOffset({ x: 0, y: 0 });
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showPhotoViewer]);

  useEffect(() => {
    if (!isOpen) {
      setShowPhotoViewer(false);
      setViewerZoom(MIN_ZOOM);
      setViewerOffset({ x: 0, y: 0 });
      setShowAddMembersModal(false);
    }
  }, [isOpen]);

  // While Group Info is open, lock page/post scrolling so wheel/touch on the
  // members list cannot scroll the feed behind the messenger.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    let lastTouchY = 0;

    const getScrollDelta = (event) => {
      if (event.type === "wheel") return event.deltaY;
      if (event.type === "touchmove" && event.touches?.[0]) {
        const currentY = event.touches[0].clientY;
        const delta = lastTouchY - currentY;
        lastTouchY = currentY;
        return delta;
      }
      return 0;
    };

    const onTouchStart = (event) => {
      if (event.touches?.[0]) {
        lastTouchY = event.touches[0].clientY;
      }
    };

    const preventBackgroundScroll = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        event.preventDefault();
        return;
      }

      // Only intercept when the gesture is inside Group Info.
      if (!target.closest(".group_info_overlay")) return;

      const scrollable = target.closest(
        ".group_info_members, .add_members_list, .transfer_admin_list"
      );
      const delta = getScrollDelta(event);

      if (scrollable && scrollable.scrollHeight > scrollable.clientHeight + 1) {
        const atTop = scrollable.scrollTop <= 0;
        const atBottom =
          scrollable.scrollTop + scrollable.clientHeight >=
          scrollable.scrollHeight - 1;
        // Allow scrolling inside the list, but block chaining at the edges.
        if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
          event.preventDefault();
        }
        return;
      }

      // Non-scrollable areas of the modal (header/footer/profile).
      event.preventDefault();
    };

    document.addEventListener("touchstart", onTouchStart, {
      passive: true,
      capture: true,
    });
    document.addEventListener("wheel", preventBackgroundScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchmove", preventBackgroundScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("touchstart", onTouchStart, {
        capture: true,
      });
      document.removeEventListener("wheel", preventBackgroundScroll, {
        capture: true,
      });
      document.removeEventListener("touchmove", preventBackgroundScroll, {
        capture: true,
      });
    };
  }, [isOpen]);

  const closeRemoveConfirm = () => {
    if (isRemovingMember) return;
    setMemberToRemove(null);
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove || !onRemoveMember || isRemovingMember) return;
    const memberId = normalizeId(memberToRemove?._id || memberToRemove?.id);
    if (!memberId) {
      setMemberToRemove(null);
      return;
    }
    try {
      setIsRemovingMember(true);
      await onRemoveMember(memberId, memberToRemove);
      setMemberToRemove(null);
    } catch (err) {
      // Parent handles user-facing error messaging; just keep the dialog open
      // so the admin can retry or cancel.
      console.error("Failed to remove member:", err);
    } finally {
      setIsRemovingMember(false);
    }
  };

  if (!isOpen || conversation?.type !== "group") return null;

  const memberToRemoveName = memberToRemove
    ? [memberToRemove?.first_name, memberToRemove?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      memberToRemove?.name ||
      memberToRemove?.username ||
      "this member"
    : "";

  return (
    <>
    <div className="group_info_overlay" onClick={onClose}>
      <div className="group_info_modal" onClick={(e) => e.stopPropagation()}>
        <div className="group_info_header">
          <h3>Group Info</h3>
          <button className="group_info_close_btn" onClick={onClose} aria-label="Close group info">
            ✕
          </button>
        </div>

        <div className="group_info_profile">
          <div className="group_info_image_wrap">
            <img
              src={groupPhotoSrc}
              alt={conversation.name || "Group Chat"}
              className={`group_info_image${hasCustomGroupPhoto ? "" : " no_custom_photo"}`}
              onClick={openPhotoViewer}
              role="button"
              tabIndex={0}
              title={hasCustomGroupPhoto ? "View group photo" : "No group profile picture"}
              aria-label={hasCustomGroupPhoto ? "View group photo" : "No group profile picture"}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openPhotoViewer();
                }
              }}
            />

            {canEditGroupPhoto && (
              <>
                <button
                  type="button"
                  className="group_photo_edit_btn"
                  title="Change group photo"
                  aria-label="Change group photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isUploadingGroupPhoto) return;
                    photoInputRef.current?.click();
                  }}
                >
                  {isUploadingGroupPhoto ? "…" : "📷"}
                </button>

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && onChangeGroupPhoto) {
                      onChangeGroupPhoto(file);
                    }
                    e.target.value = "";
                  }}
                />
              </>
            )}
          </div>

          <h4 className="group_info_name">{conversation.name || "Group Chat"}</h4>
          <span className="group_info_count">{members.length} members</span>

          {isCurrentUserAdmin && !userLeft && onAddMembers && (
            <button
              type="button"
              className="group_info_add_members_btn"
              onClick={() => setShowAddMembersModal(true)}
              aria-label="Add members to this group"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              Add Members
            </button>
          )}
        </div>

        <div className="group_info_members">
          {members.map((member) => {
            const memberId = normalizeId(member?._id || member?.id);
            const roleLabel = getRoleLabel(member);
            const memberKey = memberId || member?.username || `${member?.first_name || "member"}-${member?.last_name || ""}`;
            const displayName = [member?.first_name, member?.last_name].filter(Boolean).join(" ").trim() || member?.name || member?.username || "Member";
            const showRemove = canRemoveMember(member);

            const openMemberProfile = () => {
              if (!memberId || !onOpenMemberProfile) return;
              onOpenMemberProfile(memberId);
            };

            return (
              <div
                key={memberKey}
                className="group_member_item"
              >
                <img
                  src={member?.picture || "/images/default_profile.png"}
                  alt={member?.first_name || "Member"}
                  className="group_member_avatar"
                  onClick={openMemberProfile}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openMemberProfile();
                    }
                  }}
                />
                <div className="group_member_info">
                  <span
                    className="group_member_name"
                    onClick={openMemberProfile}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openMemberProfile();
                      }
                    }}
                  >
                    {displayName}
                  </span>
                </div>
                <span className={`group_member_action ${roleLabel === "Admin" ? "is_admin" : ""}`}>
                  {roleLabel}
                </span>
                {showRemove && (
                  <button
                    type="button"
                    className="group_member_remove_btn"
                    title="Remove member."
                    aria-label={`Remove ${displayName} from group`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMemberToRemove(member);
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="17" y1="11" x2="22" y2="11" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {userLeft ? (
          <div className="group_info_footer">
            <div className="group_info_left_notice" role="status">
              You are no longer a member of this group. Past messages remain
              available, but you can&apos;t send or receive new ones.
            </div>
            {onDeleteGroup && (
              <button
                type="button"
                className="leave_group_btn delete_group_btn"
                onClick={onDeleteGroup}
              >
                <span className="leave_group_btn_icon" aria-hidden="true">🗑️</span>
                Delete Group
              </button>
            )}
          </div>
        ) : onLeaveGroup ? (
          <div className="group_info_footer">
            <button
              type="button"
              className="leave_group_btn"
              onClick={onLeaveGroup}
            >
              <span className="leave_group_btn_icon" aria-hidden="true">🚪</span>
              Leave Group
            </button>
          </div>
        ) : null}
      </div>

      <AddMembersModal
        isVisible={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        currentUser={currentUser}
        groupName={conversation?.name || "this group"}
        existingMemberIds={[
          ...members.map((m) => m?._id || m?.id),
          ownerId,
          currentUserId,
        ].filter(Boolean)}
        onConfirm={async (selectedFriends) => {
          if (!onAddMembers) return;
          await onAddMembers(selectedFriends);
          setShowAddMembersModal(false);
        }}
      />

      {memberToRemove && (
        <div
          className="leave_group_modal_overlay remove_member_modal_overlay"
          onClick={closeRemoveConfirm}
        >
          <div
            className="leave_group_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="leave_group_header">
              <h3>Remove Member</h3>
              <button
                className="close_btn"
                onClick={closeRemoveConfirm}
                disabled={isRemovingMember}
                aria-label="Close remove member dialog"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <div className="leave_group_content">
              <div className="leave_group_icon" aria-hidden="true">⚠️</div>
              <p className="leave_group_description">
                Remove <strong>{memberToRemoveName}</strong> from this group?
              </p>
              <p className="leave_group_note">
                They will no longer be able to send or receive new messages.
              </p>
            </div>

            <div className="leave_group_actions">
              <button
                className="cancel_btn"
                onClick={closeRemoveConfirm}
                disabled={isRemovingMember}
              >
                Cancel
              </button>
              <button
                className="leave_btn delete_btn"
                onClick={handleConfirmRemove}
                disabled={isRemovingMember}
              >
                {isRemovingMember ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {showPhotoViewer && hasCustomGroupPhoto &&
      createPortal(
        <div
          className="group_photo_viewer_overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Group photo viewer"
          onClick={closePhotoViewer}
        >
          <button
            type="button"
            className="group_photo_viewer_close"
            onClick={closePhotoViewer}
            aria-label="Close photo viewer"
          >
            ✕
          </button>

          <div
            className="group_photo_viewer_toolbar"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="group_photo_viewer_zoom_btn"
              onClick={() => zoomBy(-ZOOM_STEP)}
              disabled={viewerZoom <= MIN_ZOOM}
              aria-label="Zoom out"
              title="Zoom out"
            >
              −
            </button>
            <span className="group_photo_viewer_zoom_label">
              {Math.round(viewerZoom * 100)}%
            </span>
            <button
              type="button"
              className="group_photo_viewer_zoom_btn"
              onClick={() => zoomBy(ZOOM_STEP)}
              disabled={viewerZoom >= MAX_ZOOM}
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
            <button
              type="button"
              className="group_photo_viewer_zoom_btn group_photo_viewer_reset_btn"
              onClick={() => {
                setViewerZoom(MIN_ZOOM);
                setViewerOffset({ x: 0, y: 0 });
              }}
              disabled={viewerZoom <= MIN_ZOOM}
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              Reset
            </button>
          </div>

          <div
            className={`group_photo_viewer_stage ${
              viewerZoom > MIN_ZOOM ? "is_zoomed" : ""
            }`}
            onClick={(e) => e.stopPropagation()}
            onWheel={handleViewerWheel}
            onPointerDown={handleViewerPointerDown}
            onPointerMove={handleViewerPointerMove}
            onPointerUp={handleViewerPointerUp}
            onPointerCancel={handleViewerPointerUp}
            onDoubleClick={handleViewerDoubleClick}
          >
            <img
              src={groupPhotoSrc}
              alt={conversation.name || "Group photo"}
              className="group_photo_viewer_img"
              draggable={false}
              style={{
                transform: `translate(${viewerOffset.x}px, ${viewerOffset.y}px) scale(${viewerZoom})`,
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
